import React from 'react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Card } from '../../components/ui/Card'
import { Chip } from '../../components/ui/Chip'
import { TabBar } from '../../components/ui/TabBar'
import { KommerSnart } from '../../components/ui/KommerSnart'
import { Button } from '../../components/ui/Button'
import { apiFetch } from '../../lib/api-client'

interface Communication {
  id: string
  renter_id: string | null
  contract_id: string | null
  channel: string
  subject: string
  status: string
  sent_at: string | null
  created_at: string
  renter_name: string | null
  renter_email: string | null
}

interface CommResponse {
  items: Communication[]
  total: number
}

const TABS = [
  { key: 'kommunikation', label: 'Kommunikation' },
  { key: 'system', label: 'Systemmeddelelser' },
]

function channelLabel(c: string): string {
  const m: Record<string, string> = { email: 'E-mail', sms: 'SMS', brev: 'Brev' }
  return m[c] ?? c
}

function channelVariant(c: string): 'blue' | 'amber' | 'gray' {
  if (c === 'email') return 'blue'
  if (c === 'sms') return 'amber'
  return 'gray'
}

function statusVariant(s: string): 'green' | 'red' | 'gray' {
  if (s === 'leveret') return 'green'
  if (s === 'fejlet') return 'red'
  return 'gray'
}

function statusLabel(s: string): string {
  const m: Record<string, string> = { leveret: 'Leveret', fejlet: 'Fejlet', pending: 'Afventer' }
  return m[s] ?? s
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' })
}

function KommunikationTab() {
  const [data, setData] = React.useState<CommResponse | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    apiFetch<CommResponse>('/api/v1/communications?limit=50')
      .then(setData)
      .catch(() => setError('Kunne ikke hente kommunikation.'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Henter…</div>
  if (error) return <div style={{ padding: 20, color: '#dc2626', fontSize: 13 }}>{error}</div>

  const items = data?.items ?? []

  if (items.length === 0) {
    return (
      <div style={{ padding: 64, textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>Ingen beskeder endnu</div>
        <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20, maxWidth: 320, margin: '0 auto 20px' }}>
          Brug knapperne øverst til at sende mail, brev eller SMS til dine lejere.
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Button size="sm" variant="secondary" disabled>Send mail & brev</Button>
          <Button size="sm" variant="secondary" disabled>Send SMS</Button>
        </div>
      </div>
    )
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ background: '#f8fafc' }}>
          {['Modtager', 'Titel', 'Kanal', 'Status', 'Afsendt'].map((h) => (
            <th key={h} style={{
              padding: '8px 20px', textAlign: 'left', fontSize: 11, fontWeight: 600,
              color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em',
              borderBottom: '1px solid #e8ecf4', whiteSpace: 'nowrap',
            }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((c, i) => (
          <tr key={c.id} style={{ borderBottom: i === items.length - 1 ? 'none' : '1px solid #e8ecf4' }}>
            <td style={{ padding: '10px 20px' }}>
              <div style={{ fontWeight: 500, color: '#0f172a' }}>{c.renter_name ?? '—'}</div>
              {c.renter_email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.renter_email}</div>}
            </td>
            <td style={{ padding: '10px 20px', color: '#0f172a' }}>{c.subject}</td>
            <td style={{ padding: '10px 20px' }}>
              <Chip variant={channelVariant(c.channel)}>{channelLabel(c.channel)}</Chip>
            </td>
            <td style={{ padding: '10px 20px' }}>
              <Chip variant={statusVariant(c.status)}>{statusLabel(c.status)}</Chip>
            </td>
            <td style={{ padding: '10px 20px', color: '#64748b' }}>{fmtDate(c.sent_at ?? c.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function KommunikationPage() {
  const [activeTab, setActiveTab] = React.useState('kommunikation')

  return (
    <AppLayout
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="sm" variant="secondary" disabled title="Kommer snart">Send mail & brev</Button>
          <Button size="sm" variant="secondary" disabled title="Kommer snart">Send SMS</Button>
        </div>
      }
    >
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>Kommunikation</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>Manuelt sendte beskeder og systemmeddelelser</div>
      </div>

      <Card padding="none">
        <TabBar tabs={TABS} active={activeTab} onChange={setActiveTab} />

        {activeTab === 'kommunikation' && <KommunikationTab />}

        {activeTab === 'system' && (
          <KommerSnart
            description="Systemmeddelelser som fakturamails, påkravsskrivelser, underskriftsanmodninger og reguleringsnotater vises her."
          />
        )}
      </Card>
    </AppLayout>
  )
}
